import React, { useEffect, useRef, useState } from 'react'
import { BreathAPIs } from '../APIs/BreathAPIs'
import { ButtonCustom } from '../BootstrapComponents/ButtonCustom'
import styles from './MFAValidation.module.css'

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const BRAND_COLOR = '#18365E';

const MFAValidation = ({ setShow2FAModal, setIs2FAverified, email, password }) => {

  const [verificationStatus, setVerificationStatus] = useState('');
  const [statusType, setStatusType] = useState('info');
  const [verifyToken, setVerifyToken] = useState('');
  const [otpDigits, setOtpDigits] = useState(new Array(OTP_LENGTH).fill(''));
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const inputRefs = useRef([]);
  const lastSubmittedOtp = useRef('');

  const otp = otpDigits.join('');

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const requestOtp = () => {
    setSendingOtp(true);
    setStatusType('info');
    setVerificationStatus('');
    BreathAPIs.send2FACode(email)
      .then(response => {
        if (response && response.status === 200 && response.data && response.data.success) {
          setOtpSent(true);
          setOtpDigits(new Array(OTP_LENGTH).fill(''));
          lastSubmittedOtp.current = '';
          setStatusType('success');
          setVerificationStatus('A 6-digit code has been sent to your email.');
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
          if (response.data.token) {
            setVerifyToken(response.data.token);
          }
          setTimeout(() => inputRefs.current[0]?.focus(), 0);
        } else {
          setStatusType('error');
          setVerificationStatus((response.data && response.data.message) || 'Failed to send the code. Please try again.');
        }
      })
      .catch(error => {
        setStatusType('error');
        setVerificationStatus(
          error?.response?.data?.tokenExpired
            ? 'Token expired. Please request a new code.'
            : (error.response?.data?.message || 'An error occurred while sending the code. Please try again.')
        );
      })
      .finally(() => setSendingOtp(false));
  };

  const verify2FA = (code) => {
    setVerifying(true);
    setStatusType('info');
    setVerificationStatus('');
    BreathAPIs.verify2FACode(code, verifyToken)
      .then(response => {
        if (response && response.data && response.data.success && response.data.verified) {
          setStatusType('success');
          setVerificationStatus('Code verified successfully.');
          setIs2FAverified(true);
          setShow2FAModal(false);
        } else {
          setStatusType('error');
          setVerificationStatus((response.data && response.data.message) || 'Incorrect code. Please try again.');
          setOtpDigits(new Array(OTP_LENGTH).fill(''));
          lastSubmittedOtp.current = '';
          inputRefs.current[0]?.focus();
        }
      })
      .catch(error => {
        setStatusType('error');
        setVerificationStatus(
          error?.response?.data?.tokenExpired
            ? 'Token expired. Please request a new code.'
            : (error.response?.data?.message || 'Incorrect code. Please try again.')
        );
        setOtpDigits(new Array(OTP_LENGTH).fill(''));
        lastSubmittedOtp.current = '';
        inputRefs.current[0]?.focus();
      })
      .finally(() => setVerifying(false));
  };

  useEffect(() => {
    if (otp.length === OTP_LENGTH && lastSubmittedOtp.current !== otp) {
      lastSubmittedOtp.current = otp;
      verify2FA(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    setOtpDigits(prev => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = new Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { next[i] = char; });
    setOtpDigits(next);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconCircle}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L4 5V11C4 16.5 7.4 21.7 12 23C16.6 21.7 20 16.5 20 11V5L12 2Z" stroke="#18365E" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M9 12L11 14L15.5 9.5" stroke="#18365E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h5 className={styles.title}>Two-Factor Authentication</h5>

      {!otpSent ? (
        <>
          <p className={styles.subtitle}>
            For your security, we need to verify it&#39;s you. Send a 6-digit verification code to <strong>{email}</strong>.
          </p>
          <ButtonCustom
            variant="primary"
            text={sendingOtp ? 'Sending Code...' : 'Send 2FA Code'}
            isLoading={sendingOtp}
            onClick={requestOtp}
            style={{ width: '100%', backgroundColor: BRAND_COLOR, borderColor: BRAND_COLOR }}
          />
        </>
      ) : (
        <>
          <p className={styles.subtitle}>Enter the 6-digit code sent to <strong>{email}</strong></p>

          <div className={styles.otpRow} onPaste={handlePaste}>
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                className={`${styles.otpBox} ${statusType === 'error' ? styles.otpBoxError : ''}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                disabled={verifying}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {verifying && (
            <div className={styles.verifyingRow}>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Verifying...
            </div>
          )}

          <div className={styles.resendRow}>
            {resendCooldown > 0 ? (
              <span className={styles.resendDisabled}>Resend code in {resendCooldown}s</span>
            ) : (
              <button
                type="button"
                className={styles.resendLink}
                onClick={requestOtp}
                disabled={sendingOtp}
              >
                {sendingOtp ? 'Resending...' : 'Resend code'}
              </button>
            )}
          </div>
        </>
      )}

      {verificationStatus && (
        <p className={`${styles.status} ${statusType === 'error' ? styles.statusError : statusType === 'success' ? styles.statusSuccess : ''}`}>
          {verificationStatus}
        </p>
      )}
    </div>
  );
}
export default MFAValidation
